import React, { useEffect, useImperativeHandle, useState, useMemo, useRef } from 'react'

import { IQuestionComponentProps, IQuestionComponentRef, QuestionViewFrame } from '../types'
import {
  IMaxdiffQuestionSchema,
  IMaxdiffResponseValidation,
  MaxdiffAnswers,
  MaxdiffFrameResponse,
  MaxdiffQuestionModelTypes,
  MaxdiffResponse
} from './types'
import useTimeline from '../../modules/timeline/useTimeline'
import useSession from '../../session/useSession'
import QuestionFrame from '../_common/QuestionFrame/QuestionFrame'
import View from './View'
import { checkAnswers, checkAnswersRanking } from './Model'
import { getMediumAssetPath, getSmallAssetPath } from '@aytm/lib/common/assets'
import imageAssetWithOptions from '@aytm/app/swidget/base/modules/preview/functions/imageAssetWithOptions'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import useSurveyLocale from '../../modules/locale/useSurveyLocale'

export const getCheckNumber = (schema: IMaxdiffQuestionSchema) => {
  if ('first' === schema.check_position) return 0
  if ('last' === schema.check_position) return schema.answers_mask.length
  if ('random' === schema.check_position)
    return Math.min(Math.round(Math.random() * schema.answers_mask.length), schema.answers_mask.length)
  if ('middle' === schema.check_position) return Math.floor(schema.answers_mask.length / 2)
  return 1
}

const EMPTY_ANSWERS: MaxdiffAnswers = []

const getBestWorstText = (id: string, type: string, t: (this: void, ...path: string[]) => string): string => {
  if ('best' === id) {
    return 'bestimage' === type ? '1' : t('questions', 'md', 'checkScreenFirstChoice')
  }
  if ('worst' === id) {
    return 'bestimage' === type ? '4' : t('questions', 'md', 'checkScreenLastChoice')
  }
  return '-'
}

const getBestWorstImageName = (id: string): string => {
  if ('best' === id) return '1'
  if ('worst' === id) return '4'
  return 'empty'
}

const createBestWorstAnswers = (
  t: (this: void, ...path: string[]) => string,
  type: string,
  itemsNumber: number
): MaxdiffAnswers =>
  checkAnswers(itemsNumber).map(id => {
    return {
      id,
      text: getBestWorstText(id, type, t),
      ...('bestimage' === type && { imageName: getBestWorstImageName(id) })
    }
  })

const createRankingAnswers = (
  t: (this: void, ...path: string[]) => string,
  type: string,
  itemsNumber: number
): MaxdiffAnswers =>
  checkAnswersRanking(itemsNumber).map((id, index) => {
    return {
      id,
      text: t('questions', 'md', 'checkScreenChoices')[index],
      ...('rankingimage' === type && { imageName: (index + 1).toString() })
    }
  })

export const checkScreenAnswers = (
  t: (this: void, ...path: string[]) => string,
  type: string,
  itemsNumber: number
): MaxdiffAnswers => {
  const isBestWorstType = 'best' === type || 'bestimage' === type
  const answers = isBestWorstType
    ? createBestWorstAnswers(t, type, itemsNumber)
    : createRankingAnswers(t, type, itemsNumber)

  return answers.sort(() => Math.random() - 0.5)
}

export const textLength = (text: string) =>
  text
    ?.split(/<[^/].*?>(.*?)<\/.*?>/g)
    .filter(x => x != '')
    .join().length || 0

const MaxdiffQuestion = React.memo(
  React.forwardRef<IQuestionComponentRef, IQuestionComponentProps<MaxdiffQuestionModelTypes>>(({ model }, ref) => {
    const [[response, responseValidation, frame], setResponse] = useState<
      [MaxdiffResponse, IMaxdiffResponseValidation, QuestionViewFrame]
    >([model.response, model.responseValidation, model.frame])

    const session = useSession()
    const timeline = useTimeline()
    const viewport = useViewport()
    const { t } = useSurveyLocale()
    const fontHeight = ((viewport.mobile ? viewport.width : (viewport.widgetWidth as number)) - 40) / 13
    const showCheckScreen =
      model.schema.check &&
      frame === model.response.checkNumber &&
      !response.checkShown &&
      ('best' === model.schema.check_method || 'bestimage' === model.schema.check_method)
    const backupAnswers = useRef<MaxdiffAnswers>([])

    const [showValidation, setShowValidation] = useState(false)

    const localSchema = useMemo(() => {
      const schema = JSON.parse(JSON.stringify(model.schema)) as IMaxdiffQuestionSchema
      if (model.schema.check && (showCheckScreen || !model.schema.answers_mask[frame as number])) {
        schema.text =
          t('questions', 'md', 'checkScreenPurpose') +
          t<(value: string, itemsNumber: number) => string>(
            'questions',
            'md',
            'checkScreenTitle'
          )(model.schema.check_method as string, 4)
        if (backupAnswers.current.length) {
          schema.answers = backupAnswers.current
        } else {
          schema.answers = checkScreenAnswers(t, model.schema.check_method, model.schema.items_per_question)
          backupAnswers.current = schema.answers
        }
      } else {
        schema.answers = model.schema.answers_mask[frame as number].reduce(
          (prev, cur) => {
            const answer = model.schema.answers.find(answer => cur === answer.id)
            answer.image = imageAssetWithOptions(session.rules?.sub?.[schema.id]?.[answer.id], answer.image)
            prev.push(answer)
            return prev
          },
          [...EMPTY_ANSWERS]
        )
      }
      if (!schema.best_worst) {
        if (schema.answers[0] && schema.answers[1])
          schema.answers[0].height = schema.answers[1].height = Math.max(
            schema.answers[0]?.image ? Math.ceil(textLength(schema.answers[0].text) / fontHeight) : 0,
            schema.answers[1]?.image ? Math.ceil(textLength(schema.answers[1].text) / fontHeight) : 0
          )
        if (schema.answers[2] && schema.answers[3])
          schema.answers[2].height = schema.answers[3].height = Math.max(
            schema.answers[2]?.image ? Math.ceil(textLength(schema.answers[2].text) / fontHeight) : 0,
            schema.answers[3]?.image ? Math.ceil(textLength(schema.answers[3].text) / fontHeight) : 0
          )
      }
      return schema
    }, [fontHeight, frame, model.schema, session.rules?.sub, showCheckScreen, t])

    useEffect(() => {
      session.enableNext(responseValidation.valid)
    }, [session, responseValidation.valid])

    useImperativeHandle<IQuestionComponentRef, IQuestionComponentRef>(ref, () => {
      return {
        tryNext() {
          if (model.responseValidation.valid || session.skipValidation) {
            if (model.schema.check && model.frame === model.response.checkNumber && !model.response.checkShown) {
              model.response.checkShown = true
              model.response = { ...model.response }
              setResponse([model.response, model.responseValidation, model.frame])
              if (model.response.checkNumber == model.schema.answers_mask.length) {
                model.timeline.frameDiscard(model.frame)
                return false
              }
              session.enableNext(false)
              session.enablePrev(session.skipValidation)
              session.scrollIntoView()
              return true
            } else {
              if (
                (model.frame as number) < model.schema.answers_mask.length - 1 ||
                (model.frame === model.response.checkNumber - 1 &&
                  model.response.checkNumber == model.schema.answers_mask.length)
              ) {
                timeline.frameCommit(model.frame)
                model.nextFrame()
                session.progress.advanceQuestionSeconds(
                  model.schema.answers_mask.length ? model.questionTime / model.schema.answers_mask.length : 0
                )
                model.navigationFrame = [(model.frame as number) + 1, model.schema.answers_mask.length]
                setResponse([model.response, model.responseValidation, model.frame])
                if (model.response.checkNumber != model.schema.answers_mask.length) timeline.frameStart(model.frame)
                session.enableNext(false)
                session.enablePrev(session.skipValidation)
                session.scrollIntoView()
                return true
              }
            }
            return false
          } else {
            session.enableNext(false)
            return true
          }
        },
        tryPrev() {
          if (session.skipValidation && (model.frame as number) > 0) {
            timeline.frameCommit(model.frame)
            model.prevFrame()
            session.progress.rollbackQuestionSeconds(
              model.schema.answers_mask.length ? model.questionTime / model.schema.answers_mask.length : 0
            )
            model.navigationFrame = [(model.frame as number) + 1, model.schema.answers_mask.length]
            timeline.frameStart(model.frame)
            setResponse([model.response, model.responseValidation, model.frame])
            return true
          }
          return false
        },
        showValidation() {
          setShowValidation(true)
        }
      }
    }, [model, session, timeline])

    const onUpdateResponse = (frameResponse: MaxdiffFrameResponse) => {
      if (showCheckScreen) {
        model.response.checkResponse = frameResponse
      } else {
        model.response.frames[model.frame as number] = frameResponse
      }
      model.response = { ...model.response }
      setResponse([model.response, model.responseValidation, model.frame])
      setShowValidation(false)
    }

    const imagesRef = useRef<string[]>(null)
    if (!imagesRef.current)
      imagesRef.current = model.schema.answers.reduce((array: string[], answer) => {
        if (model.schema.image_grid_columns || (answer?.image && !answer.image.expand))
          array.push(
            model.schema.image_grid_columns ? getMediumAssetPath(answer.image) : getSmallAssetPath(answer.image)
          )
        return array
      }, [])

    return (
      <QuestionFrame model={model} images={imagesRef.current} titleText={localSchema?.text}>
        <View
          schema={localSchema}
          responseValidation={responseValidation}
          frame={model.frame}
          response={showCheckScreen ? response.checkResponse : response.frames[model.frame as number] || {}}
          timeline={timeline}
          showValidation={showValidation}
          onUpdateResponse={onUpdateResponse}
        />
      </QuestionFrame>
    )
  }),
  (prevProps, nextProps) => prevProps.model === nextProps.model
)

export default MaxdiffQuestion
